/**
 * @fileoverview Edit User form.
 * @description The Edit dialog's form for one version of the user's details: the read-only facts,
 * the name and mobile number, the State, and the placement (editable for an ACTIVE user, locked
 * for anyone else). Presentational: the form logic is in useEditUserForm.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import EditUserLockedPlacement from '@/features/users/components/EditUserLockedPlacement'
import EditUserPlacementFields from '@/features/users/components/EditUserPlacementFields'
import EditUserReadOnlyFields from '@/features/users/components/EditUserReadOnlyFields'
import EditUserStateField from '@/features/users/components/EditUserStateField'
import { EDIT_FORM_ID } from '@/features/users/constants/lifecycle.constants'
import { useEditUserForm } from '@/features/users/hooks/useEditUserForm'
import FormField from '@/shared/components/molecules/FormField'
import { MOBILE_NUMBER_INPUT_PROPS } from '@/shared/lib/validation/contact-fields.constants'

import type { UpdateUserRequest, UserDetail } from '@/features/users/types/users.types'

interface EditUserFormProps {
  user: UserDetail
  onSubmit: (payload: UpdateUserRequest) => void
}

/**
 * @component
 * @param {EditUserFormProps} props - The user, and what to do with the changed fields.
 * @param {UserDetail} props.user - The user being edited, as this version of the form is filled.
 * @param {(payload: UpdateUserRequest) => void} props.onSubmit - Called with the changed fields
 * once the form is valid.
 */
function EditUserForm({ user, onSubmit: submitChanges }: EditUserFormProps) {
  const { t } = useTranslation()
  const logic = useEditUserForm(user, submitChanges)
  const { form } = logic
  const { register } = form

  return (
    <form
      id={EDIT_FORM_ID}
      data-testid="edit-user-form"
      onSubmit={logic.onSubmit}
      noValidate
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
    >
      <EditUserReadOnlyFields user={user} />

      <FormField
        id="editUserFirstName"
        data-testid="edit-user-first-name-input"
        size="md"
        label={`${t('sales.userManagement.editUserForm.fields.firstName')} *`}
        error={logic.firstNameError}
        {...register('firstName')}
      />
      <FormField
        id="editUserLastName"
        data-testid="edit-user-last-name-input"
        size="md"
        label={`${t('sales.userManagement.editUserForm.fields.lastName')} *`}
        error={logic.lastNameError}
        {...register('lastName')}
      />
      <FormField
        id="editUserMiddleName"
        data-testid="edit-user-middle-name-input"
        size="md"
        label={t('sales.userManagement.editUserForm.fields.middleName')}
        error={logic.middleNameError}
        {...register('middleName')}
      />
      <FormField
        id="editUserMobileNumber"
        data-testid="edit-user-phone-input"
        size="md"
        label={t('sales.userManagement.editUserForm.fields.phone')}
        placeholder={t('sales.userManagement.editUserForm.fields.phonePlaceholder')}
        error={logic.mobileNumberError}
        {...MOBILE_NUMBER_INPUT_PROPS}
        {...register('mobileNumber')}
      />
      <EditUserStateField form={form} user={user} />

      {logic.placementEditable ? (
        <EditUserPlacementFields form={form} userId={user.userId} />
      ) : (
        <EditUserLockedPlacement user={user} />
      )}
    </form>
  )
}

export default EditUserForm
